import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEcosystems } from "../../../api/dataSources";
import { defaultFilterState } from "../../../constants/defaultFilterState";
import { SearchFilters } from "../../../models/search";
import { GeoFeatureData } from "./EcosystemMap";
import useUserContext from "../../Authorization/useUserContext";

/**
 * Cache duration for ecosystem data queries.
 * Set to 2 minutes to reduce redundant API calls during normal user interaction
 * while still keeping data reasonably fresh. This improves initial page load
 * performance by preventing duplicate requests when components re-render.
 */
const TWO_MINUTES = 2 * 60 * 1000;

export default function useGetEcosystemData(filterState: SearchFilters) {
  const user = useUserContext();

  const ecosystem_filters = filterState.base_filters?.ecosystem_name || [];

  // Remove Ecosystem filter from the copy of the filters we will use to query for ecosystems, so
  // that unselected states (ecosystems) can retain their document count even when not selected.
  const filtersExcludingEcosystem = ((state) => {
    const filters = { ...state };
    filters.base_filters = { ...filters.base_filters, ecosystem_name: undefined };
    return filters;
  })(filterState);

  const { data: dataResponse, isLoading } = useQuery({
    queryKey: ["ecosystem_response", JSON.stringify(filtersExcludingEcosystem), user.selectedRoles],
    queryFn: () => getEcosystems(filtersExcludingEcosystem || defaultFilterState, user),
    enabled: !!filterState,
    staleTime: TWO_MINUTES,
  });

  const data = useMemo(
    () =>
      (dataResponse?.data || []).map((ecosystem) => {
        const ecosystemIsAppliedFilter = ecosystem_filters.includes(ecosystem.name);
        return {
          id: ecosystem.name,
          rate: ecosystem.record_count,
          selected: ecosystemIsAppliedFilter,
        } as GeoFeatureData;
      }),
    [filterState, dataResponse],
  );

  return { data, isLoading };
}
